import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import MDSpinner from "react-md-spinner";
import GoogleMapReact from "google-map-react";
import axios from "axios";
import {
  FaChevronDown,
  FaChevronRight,
  FaCompass,
  FaCut,
  FaEdit,
  FaInfoCircle,
  FaPlus,
  FaTrashAlt,
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";

import MapViewportToolbar from "../../components/MapViewportToolbar";
import myConfig from "../../configs/config";
import TrackHeadingMapModal from "../PendingSchedules/TrackHeadingMapModal";
import {
  deleteGroupFarm,
  fetchGroupFarms,
  fetchGroupLandPlots,
  landPlotsForFarms,
  updateGroupFarm,
} from "../PendingSchedules/serviceOrderLocationApi";
import {
  createTrackHeadingPreset,
  fetchPublishedFarmMapTrackHeadings,
  unpublishTrackHeadingFromFarmMap,
  updateTrackHeadingPreset,
} from "../PendingSchedules/trackHeadingApi";
import {
  formatTrackHeadingSummary,
  getMapOptions,
} from "../PendingSchedules/rumoPistaUtils";
import { clampSize, useDragResize } from "./useDragResize";
import { getApiErrorMessage } from "../../helpers/utils";
import {
  MAP_VIEWPORT_SCOPES,
  applyViewportToGoogleMap,
  readLocalMapViewport,
  readLocalMapLayout,
  readViewportFromGoogleMap,
  resolveMapViewport,
  writeLocalMapLayout,
  writeLocalMapViewport,
} from "../../utils/mapViewport";
import { saveFarmMapViewport } from "../../utils/mapViewportApi";
import {
  fetchFarmMapSyncStatus,
  publishFarmMapSync,
} from "../../utils/farmMapSyncApi";
import {
  buildPlotName,
  cropDisplayName,
  formatAreaHa,
  formatDistanceM,
  getPlotBaseName,
  getPlotDisplayLabel,
  isGroupSubFarm,
  landPlotIdsForSubFarm,
  mergeLandPlotLists,
  resolveSubFarmIdForPlot,
} from "../../utils/mapMeasurements";
import FarmMapDrawingPanel from "./FarmMapDrawingPanel";
import FarmMapDeleteConfirmModal from "./FarmMapDeleteConfirmModal";
import FarmMapSyncLeaveModal from "./FarmMapSyncLeaveModal";
import FarmMapKmlImport from "./FarmMapKmlImport";
import { useFarmMapDrawingTools } from "./useFarmMapDrawingTools";
import { useFarmMapPublishGuard } from "./useFarmMapPublishGuard";
import SidebarToolInfoModal from "../../components/SidebarToolInfoModal";
import {
  FARM_MAP_HELP_TITLE,
  FARM_MAP_SYNC_HELP_TITLE,
  FarmMapHelpContent,
  FarmMapSyncHelpContent,
} from "./farmMapHelpContent";
import "../RumoPistaAB/RumoPistaAB.css";
import "./FarmMapTrackHeadings.css";
import "./FarmMapDrawingPanel.css";

const DEFAULT_LIST_WIDTH = 320;
const DEFAULT_MAP_HEIGHT = 360;

function abExpandedStorageKey(farmId) {
  return `farm-map-ab-expanded-${farmId}`;
}

function readAbExpanded(farmId) {
  try {
    return window.localStorage.getItem(abExpandedStorageKey(farmId)) === "1";
  } catch (error) {
    return false;
  }
}

function readFarmMapLayout(farmId) {
  const maxListWidth = Math.min(560, window.innerWidth * 0.45);
  const maxMapHeight = Math.min(900, window.innerHeight * 0.75);
  const saved = readLocalMapLayout(MAP_VIEWPORT_SCOPES.FARM_MAP, farmId);
  const savedListWidth =
    saved && saved.listWidth != null ? saved.listWidth : DEFAULT_LIST_WIDTH;
  const savedMapHeight =
    saved && saved.mapHeight != null ? saved.mapHeight : DEFAULT_MAP_HEIGHT;
  return {
    listWidth: clampSize(savedListWidth, 240, maxListWidth),
    mapHeight: clampSize(savedMapHeight, 240, maxMapHeight),
  };
}

function FarmMapTrackHeadings() {
  const { farm_id: farmId } = useParams();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [farmCenter, setFarmCenter] = useState(null);
  const [savedMapViewport, setSavedMapViewport] = useState(null);
  const [headings, setHeadings] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [visibleMapIds, setVisibleMapIds] = useState([]);
  const [editSummary, setEditSummary] = useState("");
  const [saving, setSaving] = useState(false);
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [mapModalMode, setMapModalMode] = useState("edit");
  const [draftBearing, setDraftBearing] = useState(null);
  const [createName, setCreateName] = useState("");
  const [createSummary, setCreateSummary] = useState("");
  const [serviceFarmId, setServiceFarmId] = useState("");
  const [landPlotIds, setLandPlotIds] = useState([]);
  const [groupFarms, setGroupFarms] = useState([]);
  const [allLandPlots, setAllLandPlots] = useState([]);
  const [mapReady, setMapReady] = useState(false);
  const [highlightedCropIds, setHighlightedCropIds] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deletingDrawing, setDeletingDrawing] = useState(false);
  const [renamingCropId, setRenamingCropId] = useState(null);
  const [editingCropName, setEditingCropName] = useState("");
  const [savingCropRename, setSavingCropRename] = useState(false);
  const [renamingSubFarmId, setRenamingSubFarmId] = useState(null);
  const [editingSubFarmName, setEditingSubFarmName] = useState("");
  const [savingSubFarmRename, setSavingSubFarmRename] = useState(false);
  const cropAreasRef = useRef([]);
  const [listWidth, setListWidth] = useState(() => readFarmMapLayout(farmId).listWidth);
  const [abExpanded, setAbExpanded] = useState(() => readAbExpanded(farmId));
  const [showFarmMapHelp, setShowFarmMapHelp] = useState(false);
  const [showSyncHelp, setShowSyncHelp] = useState(false);
  const [mapHeight, setMapHeight] = useState(() => readFarmMapLayout(farmId).mapHeight);
  const [farmMapSyncRevision, setFarmMapSyncRevision] = useState(0);
  const [farmMapSyncPublishedAt, setFarmMapSyncPublishedAt] = useState(null);
  const [publishingFarmMapSync, setPublishingFarmMapSync] = useState(false);
  const mapApiRef = useRef(null);
  const mapsApiRef = useRef(null);
  const overlaysRef = useRef([]);
  const listWidthRef = useRef(listWidth);
  const mapHeightRef = useRef(mapHeight);
  const clearUnpublishedRef = useRef(() => {});

  const handlePublishFarmMapSync = useCallback(async () => {
    setPublishingFarmMapSync(true);
    try {
      const result = await publishFarmMapSync(farmId);
      setFarmMapSyncRevision(result.syncRevision || 0);
      setFarmMapSyncPublishedAt(result.publishedAt || null);
      clearUnpublishedRef.current();
      toast.success(
        result.detail ||
          t("Mapa publicado. Os tablets vão sincronizar na próxima verificação."),
        { position: "bottom-right", autoClose: 8000 }
      );
      return result;
    } catch (error) {
      toast.error(
        getApiErrorMessage(error, t("Não foi possível publicar o mapa para o app.")),
        { position: "bottom-right", autoClose: 10000 }
      );
      throw error;
    } finally {
      setPublishingFarmMapSync(false);
    }
  }, [farmId, t]);

  const publishGuard = useFarmMapPublishGuard({
    onPublish: handlePublishFarmMapSync,
  });

  clearUnpublishedRef.current = publishGuard.clearUnpublished;
  const markMapUnpublished = publishGuard.markUnpublished;

  useEffect(() => {
    publishGuard.clearUnpublished();
  }, [farmId, publishGuard.clearUnpublished]);

  useEffect(() => {
    listWidthRef.current = listWidth;
  }, [listWidth]);

  useEffect(() => {
    mapHeightRef.current = mapHeight;
  }, [mapHeight]);

  useEffect(() => {
    const layout = readFarmMapLayout(farmId);
    listWidthRef.current = layout.listWidth;
    mapHeightRef.current = layout.mapHeight;
    setListWidth(layout.listWidth);
    setMapHeight(layout.mapHeight);
  }, [farmId]);

  const handlePanelResize = useCallback((axis, dx, dy) => {
    const maxListWidth = Math.min(560, window.innerWidth * 0.45);
    const maxMapHeight = Math.min(900, window.innerHeight * 0.75);

    if (axis === "list") {
      setListWidth((current) => {
        const next = clampSize(current + dx, 240, maxListWidth);
        listWidthRef.current = next;
        return next;
      });
    }
    if (axis === "map-e") {
      setListWidth((current) => {
        const next = clampSize(current - dx, 240, maxListWidth);
        listWidthRef.current = next;
        return next;
      });
    }
    if (axis === "map-s" || axis === "map-se") {
      setMapHeight((current) => {
        const next = clampSize(current + dy, 240, maxMapHeight);
        mapHeightRef.current = next;
        return next;
      });
    }
    if (axis === "map-se") {
      setListWidth((current) => {
        const next = clampSize(current - dx, 240, maxListWidth);
        listWidthRef.current = next;
        return next;
      });
    }
  }, []);

  const persistMapLayout = useCallback(() => {
    if (!farmId) return;
    writeLocalMapLayout(MAP_VIEWPORT_SCOPES.FARM_MAP, farmId, {
      listWidth: listWidthRef.current,
      mapHeight: mapHeightRef.current,
    });
  }, [farmId]);

  const { onResizeStart } = useDragResize(handlePanelResize, persistMapLayout);

  const selectSubFarm = useCallback(
    (subFarmId) => {
      const normalizedId = String(subFarmId);
      const isAlreadySelected =
        groupFarms.some(
          (farm) =>
            isGroupSubFarm(farm) && String(farm.id) === String(serviceFarmId)
        ) && String(serviceFarmId) === normalizedId;

      if (isAlreadySelected) {
        setServiceFarmId(String(farmId));
        setHighlightedCropIds([]);
        return;
      }

      setServiceFarmId(normalizedId);
      const plots = mergeLandPlotLists(allLandPlots, cropAreasRef.current);
      setHighlightedCropIds(
        landPlotIdsForSubFarm(plots, subFarmId, groupFarms)
      );
    },
    [allLandPlots, groupFarms, serviceFarmId, farmId]
  );

  const highlightedSubFarmId = useMemo(() => {
    if (!serviceFarmId) return null;
    const isSubFarm = groupFarms.some(
      (farm) =>
        isGroupSubFarm(farm) && String(farm.id) === String(serviceFarmId)
    );
    if (!isSubFarm) return null;

    const plots = mergeLandPlotLists(allLandPlots, cropAreasRef.current);
    const subFarmPlotIds = landPlotIdsForSubFarm(
      plots,
      serviceFarmId,
      groupFarms
    );

    if (subFarmPlotIds.length === 0) {
      return highlightedCropIds.length === 0 ? serviceFarmId : null;
    }

    const allPlotsHighlighted =
      subFarmPlotIds.every((id) => highlightedCropIds.includes(id)) &&
      highlightedCropIds.length === subFarmPlotIds.length;

    return allPlotsHighlighted ? serviceFarmId : null;
  }, [serviceFarmId, groupFarms, highlightedCropIds, allLandPlots]);

  const handleCropClick = useCallback(
    (cropId) => {
      setHighlightedCropIds((current) => {
        const isRemoving = current.includes(cropId);
        const next = isRemoving
          ? current.filter((id) => id !== cropId)
          : current.concat(cropId);

        if (!isRemoving) {
          const plot = allLandPlots.find((item) => item.id === cropId);
          const linkedSubFarmId = resolveSubFarmIdForPlot(plot, groupFarms);
          if (linkedSubFarmId) {
            setServiceFarmId(String(linkedSubFarmId));
          }
        } else if (next.length === 0) {
          setServiceFarmId(String(farmId));
        } else {
          const linkedSubFarmIds = [
            ...new Set(
              next
                .map((id) =>
                  resolveSubFarmIdForPlot(
                    allLandPlots.find((item) => item.id === id),
                    groupFarms
                  )
                )
                .filter(Boolean)
                .map(String)
            ),
          ];
          if (linkedSubFarmIds.length === 1) {
            setServiceFarmId(linkedSubFarmIds[0]);
          }
        }

        return next;
      });
    },
    [allLandPlots, groupFarms, farmId]
  );

  const drawingTools = useFarmMapDrawingTools({
    mapRef: mapApiRef,
    mapsRef: mapsApiRef,
    farmId,
    mapReady,
    highlightedCropIds,
    onCropClick: handleCropClick,
    allLandPlots,
    groupFarms,
    highlightedSubFarmId,
    onSubFarmClick: selectSubFarm,
    onUnpublishedChange: markMapUnpublished,
    onHoleFinished: (holeCount) => {
      toast.info(
        t(
          "Recorte {{count}} adicionado. Clique em Salvar talhão para persistir.",
          { count: holeCount }
        ),
        { position: "bottom-right", autoClose: 6000 }
      );
    },
    onHoleFinishFailed: () => {
      toast.warning(
        t("O recorte precisa de pelo menos 3 pontos. Continue desenhando no mapa."),
        { position: "bottom-right", autoClose: 5000 }
      );
    },
  });

  const subFarmPlotCounts = useMemo(() => {
    const plots = mergeLandPlotLists(allLandPlots, drawingTools.cropAreas);
    const counts = {};
    groupFarms.filter(isGroupSubFarm).forEach((farm) => {
      counts[farm.id] = landPlotIdsForSubFarm(
        plots,
        farm.id,
        groupFarms
      ).length;
    });
    return counts;
  }, [allLandPlots, groupFarms, drawingTools.cropAreas]);

  cropAreasRef.current = drawingTools.cropAreas;

  const selectedCropSummary = useMemo(() => {
    const selected = drawingTools.cropAreas.filter((crop) =>
      highlightedCropIds.includes(crop.id)
    );
    const totalHa = selected.reduce(
      (sum, crop) => sum + (Number(crop.area_h2) || 0),
      0
    );
    return { count: selected.length, totalHa };
  }, [drawingTools.cropAreas, highlightedCropIds]);

  function plotSidebarLabel(crop) {
    const pending = drawingTools.pendingShape;
    if (
      pending &&
      pending.type === "polygon" &&
      pending.editCropId != null &&
      String(pending.editCropId) === String(crop.id) &&
      Number.isFinite(Number(pending.areaHa))
    ) {
      return buildPlotName(getPlotBaseName(crop), pending.areaHa);
    }
    return getPlotDisplayLabel(crop);
  }

  function plotSidebarHectares(crop) {
    const pending = drawingTools.pendingShape;
    if (
      pending &&
      pending.type === "polygon" &&
      pending.editCropId != null &&
      String(pending.editCropId) === String(crop.id) &&
      Number.isFinite(Number(pending.areaHa))
    ) {
      return Number(pending.areaHa).toFixed(2);
    }
    const hectares = Number(crop.area_h2);
    return Number.isFinite(hectares) ? hectares.toFixed(2) : "0.00";
  }

  async function handleSaveDrawingFarm(name) {
    const trimmedName = (name || "").trim();
    const isUpdate =
      drawingTools.pendingShape && drawingTools.pendingShape.editSubFarmId;
    if (!isUpdate && !trimmedName) {
      toast.error(t("Informe o nome da fazenda."), { position: "bottom-right" });
      return;
    }
    try {
      const saved = await drawingTools.savePendingFarm(trimmedName);
      const savedName =
        (saved && saved.nome_da_fazenda) ||
        trimmedName ||
        (drawingTools.pendingShape && drawingTools.pendingShape.subFarmName) ||
        "";
      const count =
        saved && Array.isArray(saved.assigned_land_plot_ids)
          ? saved.assigned_land_plot_ids.length
          : 0;
      toast.success(
        isUpdate
          ? count > 0
            ? `${t('Perímetro de "{{name}}" atualizado.', { name: savedName })} ${count} ${t("talhão(ões) vinculado(s).")}`
            : t('Perímetro de "{{name}}" atualizado.', { name: savedName })
          : count > 0
            ? `${t('Fazenda "{{name}}" salva.', { name: savedName })} ${count} ${t("talhão(ões) vinculado(s).")}`
            : t('Fazenda "{{name}}" salva no mapa.', { name: savedName }),
        { position: "bottom-right" }
      );
      const farms = await fetchGroupFarms(farmId);
      setGroupFarms(farms);
      const plots = await fetchGroupLandPlots(farmId);
      setAllLandPlots(plots);
      await drawingTools.reloadDrawings();
      if (saved && saved.id) {
        const mergedPlots = mergeLandPlotLists(
          plots,
          drawingTools.cropAreas
        );
        setServiceFarmId(String(saved.id));
        setHighlightedCropIds(
          landPlotIdsForSubFarm(mergedPlots, saved.id, farms)
        );
      }
      drawingTools.closeTool();
    } catch (error) {
      toast.error(
        isUpdate
          ? t("Não foi possível atualizar o perímetro da fazenda.")
          : t("Não foi possível salvar a fazenda."),
        {
          position: "bottom-right",
        }
      );
    }
  }

  async function handleSaveDrawingCrop(cropName) {
    const isUpdate =
      drawingTools.pendingShape && drawingTools.pendingShape.editCropId;
    const holeCount =
      drawingTools.pendingShape && drawingTools.pendingShape.holes
        ? drawingTools.pendingShape.holes.length
        : 0;
    try {
      await drawingTools.savePendingCrop(cropName);
      const plots = await fetchGroupLandPlots(farmId);
      setAllLandPlots(plots);
      if (highlightedSubFarmId) {
        setHighlightedCropIds(
          landPlotIdsForSubFarm(
            mergeLandPlotLists(plots, cropAreasRef.current),
            highlightedSubFarmId,
            groupFarms
          )
        );
      }
      toast.success(
        isUpdate
          ? holeCount > 0
            ? t("Talhão atualizado no mapa com {{count}} recorte(s) interno(s).", {
                count: holeCount,
              })
            : t("Talhão atualizado no mapa.")
          : holeCount > 0
            ? t("Talhão salvo com {{count}} recorte(s) interno(s). Clique em Sincronizar para enviar ao app.", {
                count: holeCount,
              })
            : t("Talhão salvo e destacado no mapa."),
        {
          position: "bottom-right",
        }
      );
    } catch (error) {
      const detail =
        (error.response &&
          error.response.data &&
          (error.response.data.detail ||
            error.response.data.area ||
            error.response.data.non_field_errors)) ||
        error.message;
      toast.error(
        typeof detail === "string"
          ? detail
          : isUpdate
            ? t("Não foi possível atualizar o talhão.")
            : t("Não foi possível salvar o talhão."),
        {
          position: "bottom-right",
          autoClose: 10000,
        }
      );
    }
  }

  function handleStartAdjustCrop(cropId) {
    setHighlightedCropIds([cropId]);
    drawingTools.startEditingCrop(cropId);
  }

  function handleStartAddingHoleToCrop(cropId) {
    setHighlightedCropIds([cropId]);
    drawingTools.startAddingHoleToCrop(cropId);
  }

  function startEditingCropName(crop, event) {
    event.preventDefault();
    event.stopPropagation();
    setRenamingCropId(crop.id);
    setEditingCropName(getPlotBaseName(crop));
  }

  function cancelEditingCropName() {
    setRenamingCropId(null);
    setEditingCropName("");
  }

  async function commitEditingCropName(cropId) {
    const trimmedName = editingCropName.trim();
    if (!trimmedName) {
      cancelEditingCropName();
      return;
    }

    const crop = drawingTools.cropAreas.find((item) => item.id === cropId);
    if (!crop || trimmedName === getPlotBaseName(crop)) {
      cancelEditingCropName();
      return;
    }

    setSavingCropRename(true);
    try {
      await drawingTools.renameCrop(cropId, trimmedName);
      toast.success(t("Nome do talhão atualizado."), {
        position: "bottom-right",
      });
      cancelEditingCropName();
    } catch (error) {
      toast.error(t("Não foi possível renomear o talhão."), {
        position: "bottom-right",
      });
    } finally {
      setSavingCropRename(false);
    }
  }

  function handleCropNameEditKeyDown(event, cropId) {
    if (event.key === "Enter") {
      event.preventDefault();
      commitEditingCropName(cropId);
    }
    if (event.key === "Escape") {
      event.preventDefault();
      cancelEditingCropName();
    }
  }

  function handleStartAdjustSubFarm(subFarm) {
    if (!subFarm || !subFarm.boundary_geojson) {
      toast.warning(
        t("Esta sub-fazenda ainda não tem perímetro. Use Desenhar fazenda."),
        { position: "bottom-right" }
      );
      return;
    }
    selectSubFarm(subFarm.id);
    drawingTools.startEditingSubFarm(subFarm.id);
  }

  function startRenamingSubFarm(farm) {
    setRenamingSubFarmId(farm.id);
    setEditingSubFarmName(farm.nome_da_fazenda || "");
  }

  function cancelEditingSubFarm() {
    setRenamingSubFarmId(null);
    setEditingSubFarmName("");
  }

  async function commitEditingSubFarmName(subFarmId) {
    const trimmedName = editingSubFarmName.trim();
    if (!trimmedName) {
      cancelEditingSubFarm();
      return;
    }

    const farm = groupFarms.find((item) => String(item.id) === String(subFarmId));
    if (!farm || trimmedName === (farm.nome_da_fazenda || "").trim()) {
      cancelEditingSubFarm();
      return;
    }

    setSavingSubFarmRename(true);
    try {
      await updateGroupFarm(farmId, subFarmId, trimmedName);
      const farms = await fetchGroupFarms(farmId);
      setGroupFarms(farms);
      cancelEditingSubFarm();
      markMapUnpublished();
      toast.success(t("Sub-fazenda atualizada."), { position: "bottom-right" });
    } catch (error) {
      const detail =
        error.response &&
        error.response.data &&
        error.response.data.nome_da_fazenda &&
        error.response.data.nome_da_fazenda[0];
      toast.error(detail || t("Não foi possível atualizar a sub-fazenda."), {
        position: "bottom-right",
      });
    } finally {
      setSavingSubFarmRename(false);
    }
  }

  function handleSubFarmNameEditKeyDown(event, subFarmId) {
    if (event.key === "Enter") {
      event.preventDefault();
      commitEditingSubFarmName(subFarmId);
    }
    if (event.key === "Escape") {
      event.preventDefault();
      cancelEditingSubFarm();
    }
  }

  function requestDeleteSubFarm(subFarm) {
    setDeleteConfirm({
      type: "subFarm",
      id: subFarm.id,
      label: subFarm.nome_da_fazenda || t("Sub-fazenda"),
    });
  }

  function requestDeleteDrawingLine(lineId) {
    const line = drawingTools.savedLines.find((item) => item.id === lineId);
    const label = line ? (line.name || t("Linha")).trim() : t("Linha");
    setDeleteConfirm({
      type: "line",
      id: lineId,
      label,
    });
  }

  function requestDeleteDrawingCrop(cropId) {
    const crop = drawingTools.cropAreas.find((item) => item.id === cropId);
    const label = crop ? cropDisplayName(crop) : t("talhão");
    setDeleteConfirm({
      type: "crop",
      id: cropId,
      label,
    });
  }

  async function handleConfirmDeleteDrawing() {
    const target = deleteConfirm;
    if (!target) return;

    setDeletingDrawing(true);
    try {
      if (target.type === "line") {
        await drawingTools.removeLine(target.id);
        toast.success(t("Linha removida."), { position: "bottom-right" });
      } else if (target.type === "subFarm") {
        await deleteGroupFarm(farmId, target.id);
        if (String(serviceFarmId) === String(target.id)) {
          setServiceFarmId(String(farmId));
        }
        const farms = await fetchGroupFarms(farmId);
        setGroupFarms(farms);
        const plots = await fetchGroupLandPlots(farmId);
        setAllLandPlots(plots);
        toast.success(t("Sub-fazenda excluída."), { position: "bottom-right" });
      } else {
        await drawingTools.removeCrop(target.id);
        setHighlightedCropIds((current) =>
          current.filter((id) => String(id) !== String(target.id))
        );
        toast.success(t("Talhão removido."), { position: "bottom-right" });
      }
      markMapUnpublished();
      setDeleteConfirm(null);
    } catch (error) {
      const reason = getApiErrorMessage(error, "");
      const fallback =
        target.type === "line"
          ? t("Não foi possível remover a linha.")
          : target.type === "subFarm"
          ? t("Não foi possível excluir a sub-fazenda.")
          : t("Não foi possível remover o talhão.");
      toast.error(reason ? `${fallback} Motivo: ${reason}` : fallback, {
        position: "bottom-right",
        autoClose: 10000,
      });
    } finally {
      setDeletingDrawing(false);
    }
  }

  useEffect(() => {
    if (!mapApiRef.current) return;
    const timer = setTimeout(() => {
      window.google.maps.event.trigger(mapApiRef.current, "resize");
    }, 50);
    return () => clearTimeout(timer);
  }, [listWidth, mapHeight, visibleMapIds, selectedId, draftBearing]);

  const visibleHeadings = useMemo(
    () => headings.filter((item) => visibleMapIds.includes(item.id)),
    [headings, visibleMapIds]
  );

  const visibleLandPlots = useMemo(
    () =>
      serviceFarmId
        ? landPlotsForFarms(allLandPlots, [parseInt(serviceFarmId, 10)])
        : [],
    [allLandPlots, serviceFarmId]
  );

  const selected = useMemo(
    () => headings.find((item) => item.id === selectedId) || null,
    [headings, selectedId]
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [centerRes, published, syncStatus] = await Promise.all([
        axios.get(`${myConfig.API_URL}/farm/${farmId}/`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("client-token")}`,
          },
        }),
        fetchPublishedFarmMapTrackHeadings(farmId),
        fetchFarmMapSyncStatus(farmId).catch(() => ({
          syncRevision: 0,
          publishedAt: null,
        })),
      ]);
      setFarmCenter(centerRes.data.farm_center);
      setSavedMapViewport(centerRes.data.map_viewport || null);
      setHeadings(published);
      setFarmMapSyncRevision(syncStatus.syncRevision || 0);
      setFarmMapSyncPublishedAt(syncStatus.publishedAt || null);
      setSelectedId((current) => {
        if (current && published.some((item) => item.id === current)) {
          return current;
        }
        return null;
      });
      setVisibleMapIds((current) =>
        current.filter((id) => published.some((item) => item.id === id))
      );
    } catch (error) {
      toast.error(t("Não foi possível carregar o mapa da fazenda."), {
        position: "bottom-right",
      });
    } finally {
      setLoading(false);
    }
  }, [farmId, t]);

  useEffect(() => {
    document.title = "Agriconnected - Mapa da fazenda";
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!farmId) return;
    fetchGroupFarms(farmId)
      .then(setGroupFarms)
      .catch(() => setGroupFarms([]));
    fetchGroupLandPlots(farmId)
      .then(setAllLandPlots)
      .catch(() => setAllLandPlots([]));
  }, [farmId]);

  useEffect(() => {
    if (serviceFarmId || !farmId) return;
    setServiceFarmId(String(farmId));
  }, [farmId, serviceFarmId]);

  useEffect(() => {
    setEditSummary(selected ? selected.summary_text || "" : "");
  }, [selected]);

  const mapHeadingCoords = useMemo(() => {
    if (draftBearing) {
      return {
        pointALat: draftBearing.pointA.lat,
        pointALng: draftBearing.pointA.lng,
        pointBLat: draftBearing.pointB.lat,
        pointBLng: draftBearing.pointB.lng,
      };
    }
    return null;
  }, [draftBearing]);

  const initialMapViewport = useMemo(
    () =>
      resolveMapViewport({
        apiViewport: savedMapViewport,
        localViewport: readLocalMapViewport(
          MAP_VIEWPORT_SCOPES.FARM_MAP,
          farmId
        ),
        fallbackCenter: farmCenter,
        fallbackZoom: 18,
        preferApi: true,
      }),
    [savedMapViewport, farmCenter, farmId]
  );

  async function handleSaveMapViewport() {
    if (!mapApiRef.current) {
      toast.error(t("Mapa ainda não está pronto."), {
        position: "bottom-right",
      });
      return;
    }

    const viewport = readViewportFromGoogleMap(mapApiRef.current);
    if (!viewport) {
      toast.error(t("Não foi possível ler a posição atual do mapa."), {
        position: "bottom-right",
      });
      return;
    }

    writeLocalMapViewport(MAP_VIEWPORT_SCOPES.FARM_MAP, farmId, viewport);
    setSavedMapViewport(viewport);

    try {
      const saved = await saveFarmMapViewport(farmId, viewport);
      setSavedMapViewport(saved);
      toast.success(t("Posição e zoom do mapa salvos para esta fazenda."), {
        position: "bottom-right",
      });
    } catch (error) {
      toast.warn(
        t(
          "Posição salva neste navegador, mas não foi possível sincronizar com o servidor."
        ),
        { position: "bottom-right" }
      );
    }
  }

  const clearMapOverlays = useCallback(() => {
    overlaysRef.current.forEach((overlay) => {
      if (overlay && overlay.setMap) overlay.setMap(null);
    });
    overlaysRef.current = [];
  }, []);

  const drawMapOverlays = useCallback(
    (map, maps, items, draftCoords, activeId) => {
      clearMapOverlays();
      if (!map || !maps) return;

      const palette = ["#2196F3", "#43A047", "#FB8C00", "#8E24AA", "#E53935"];

      items.forEach((item, index) => {
        if (item.point_a_lat == null || item.point_b_lat == null) return;

        const isActive = item.id === activeId;
        const color = isActive ? "#1565C0" : palette[index % palette.length];

        overlaysRef.current.push(
          new maps.Marker({
            position: { lat: item.point_a_lat, lng: item.point_a_lng },
            map,
            label: { text: "A", color: "white", fontWeight: "bold" },
          }),
          new maps.Marker({
            position: { lat: item.point_b_lat, lng: item.point_b_lng },
            map,
            label: { text: "B", color: "white", fontWeight: "bold" },
          }),
          new maps.Polyline({
            path: [
              { lat: item.point_a_lat, lng: item.point_a_lng },
              { lat: item.point_b_lat, lng: item.point_b_lng },
            ],
            geodesic: true,
            strokeColor: color,
            strokeOpacity: 1,
            strokeWeight: isActive ? 5 : 3,
            map,
          })
        );
      });

      if (draftCoords) {
        overlaysRef.current.push(
          new maps.Marker({
            position: { lat: draftCoords.pointALat, lng: draftCoords.pointALng },
            map,
            label: { text: "A", color: "white", fontWeight: "bold" },
          }),
          new maps.Marker({
            position: { lat: draftCoords.pointBLat, lng: draftCoords.pointBLng },
            map,
            label: { text: "B", color: "white", fontWeight: "bold" },
          }),
          new maps.Polyline({
            path: [
              { lat: draftCoords.pointALat, lng: draftCoords.pointALng },
              { lat: draftCoords.pointBLat, lng: draftCoords.pointBLng },
            ],
            geodesic: true,
            strokeColor: "#FF9800",
            strokeOpacity: 1,
            strokeWeight: 4,
            map,
          })
        );
      }
    },
    [clearMapOverlays]
  );

  useEffect(() => {
    if (!mapReady || !mapApiRef.current || !window.google) return;
    drawMapOverlays(
      mapApiRef.current,
      window.google.maps,
      visibleHeadings,
      mapHeadingCoords,
      selectedId
    );
  }, [
    mapReady,
    visibleHeadings,
    mapHeadingCoords,
    selectedId,
    drawMapOverlays,
  ]);

  function toggleHeadingVisibility(id) {
    cancelCreate();
    setSelectedId(id);
    setVisibleMapIds((current) =>
      current.includes(id)
        ? current.filter((itemId) => itemId !== id)
        : [...current, id]
    );
  }

  async function handleSaveSummary() {
    if (!selected) return;
    setSaving(true);
    try {
      const updated = await updateTrackHeadingPreset(selected.id, {
        summary_text: editSummary.trim(),
      });
      setHeadings((items) =>
        items.map((item) => (item.id === updated.id ? updated : item))
      );
      markMapUnpublished();
      toast.success(t("Recomendação atualizada no mapa da fazenda."), {
        position: "bottom-right",
      });
    } catch (error) {
      toast.error(t("Não foi possível salvar a recomendação."), {
        position: "bottom-right",
      });
    } finally {
      setSaving(false);
    }
  }

  async function handleMapConfirm(bearingData) {
    if (mapModalMode === "create") {
      const serviceFarm = groupFarms.find(
        (item) => String(item.id) === String(serviceFarmId || farmId)
      );
      setDraftBearing(bearingData);
      setCreateName((current) => current || t("Rumo A-B"));
      setCreateSummary(
        formatTrackHeadingSummary(
          bearingData,
          serviceFarm ? serviceFarm.nome_da_fazenda : ""
        )
      );
      setSelectedId(null);
      setMapModalOpen(false);
      return;
    }

    if (!selected) return;
    setSaving(true);
    try {
      const updated = await updateTrackHeadingPreset(selected.id, {
        point_a_lat: bearingData.pointA.lat,
        point_a_lng: bearingData.pointA.lng,
        point_b_lat: bearingData.pointB.lat,
        point_b_lng: bearingData.pointB.lng,
        bearing: bearingData.bearing,
        distance_m: bearingData.distance,
        published_on_farm_map: true,
      });
      setHeadings((items) =>
        items.map((item) => (item.id === updated.id ? updated : item))
      );
      markMapUnpublished();
      toast.success(t("Rumo A-B atualizado no mapa da fazenda."), {
        position: "bottom-right",
      });
    } catch (error) {
      toast.error(t("Não foi possível atualizar o rumo A-B."), {
        position: "bottom-right",
      });
    } finally {
      setSaving(false);
    }
  }

  function openCreateModal() {
    setMapModalMode("create");
    setSelectedId(null);
    setDraftBearing(null);
    setCreateName("");
    setCreateSummary("");
    setServiceFarmId(String(farmId));
    setLandPlotIds([]);
    setMapModalOpen(true);
  }

  function cancelCreate() {
    setDraftBearing(null);
    setCreateName("");
    setCreateSummary("");
    setLandPlotIds([]);
  }

  async function handleCreatePublish() {
    if (!draftBearing) {
      toast.error(t("Defina os pontos A e B no mapa antes de publicar."), {
        position: "bottom-right",
      });
      return;
    }
    if (!createName.trim()) {
      toast.error(t("Informe um nome para o rumo A-B."), {
        position: "bottom-right",
      });
      return;
    }
    if (!serviceFarmId) {
      toast.error(t("Selecione a fazenda do serviço."), {
        position: "bottom-right",
      });
      return;
    }

    setSaving(true);
    try {
      const created = await createTrackHeadingPreset({
        context_farm: parseInt(farmId, 10),
        service_farm: parseInt(serviceFarmId, 10),
        name: createName.trim(),
        service_type: "",
        point_a_lat: draftBearing.pointA.lat,
        point_a_lng: draftBearing.pointA.lng,
        point_b_lat: draftBearing.pointB.lat,
        point_b_lng: draftBearing.pointB.lng,
        bearing: draftBearing.bearing,
        distance_m: draftBearing.distance,
        summary_text: createSummary.trim(),
        land_plot_ids: landPlotIds,
        published_on_farm_map: true,
      });
      toast.success(t("Rumo A-B publicado no mapa da fazenda."), {
        position: "bottom-right",
      });
      markMapUnpublished();
      cancelCreate();
      await loadData();
      setSelectedId(created.id);
      setVisibleMapIds((current) =>
        current.includes(created.id) ? current : [...current, created.id]
      );
    } catch (error) {
      toast.error(t("Não foi possível publicar o rumo A-B no mapa."), {
        position: "bottom-right",
      });
    } finally {
      setSaving(false);
    }
  }

  async function handleUnpublish() {
    if (!selected) return;
    const label = selected.name || t("Rumo A-B");
    const confirmed = window.confirm(
      t(
        "Tem certeza que deseja apagar o rumo A-B \"{{name}}\" do mapa da fazenda? A ordem de serviço concluída não será afetada.",
        { name: label }
      )
    );
    if (!confirmed) return;

    setSaving(true);
    try {
      await unpublishTrackHeadingFromFarmMap(selected.id);
      markMapUnpublished();
      toast.success(t("Rumo A-B removido do mapa da fazenda."), {
        position: "bottom-right",
      });
      await loadData();
    } catch (error) {
      toast.error(t("Não foi possível remover o rumo do mapa."), {
        position: "bottom-right",
      });
    } finally {
      setSaving(false);
    }
  }

  const initialBearingData =
    mapModalMode === "create"
      ? draftBearing
      : selected &&
        selected.point_a_lat != null &&
        selected.point_a_lng != null &&
        selected.point_b_lat != null &&
        selected.point_b_lng != null
      ? {
          pointA: { lat: selected.point_a_lat, lng: selected.point_a_lng },
          pointB: { lat: selected.point_b_lat, lng: selected.point_b_lng },
          bearing: selected.bearing,
          distance: selected.distance_m,
        }
      : null;

  const isCreating = Boolean(draftBearing) && !selected;

  function renderFarmMap() {
    return (
      <div className="farm-map-page__map" style={{ height: mapHeight }}>
        {!loading ? (
          <GoogleMapReact
            key={`farm-map-${farmId}`}
            bootstrapURLKeys={{
              key: myConfig.GOOGLE_API_KEY,
              libraries: ["geometry"],
            }}
            options={getMapOptions}
            defaultCenter={{
              lat: initialMapViewport.lat,
              lng: initialMapViewport.lng,
            }}
            defaultZoom={initialMapViewport.zoom}
            yesIWantToUseGoogleMapApiInternals
            onGoogleApiLoaded={(google) => {
              mapApiRef.current = google.map;
              mapsApiRef.current = google.maps;
              if (!window.google) {
                window.google = google;
              }
              applyViewportToGoogleMap(google.map, initialMapViewport);
              setMapReady(true);
            }}
          />
        ) : (
          <div className="farm-map-page__spinner">
            <MDSpinner />
          </div>
        )}
        <MapViewportToolbar
          onSave={handleSaveMapViewport}
          disabled={loading || !mapReady || saving}
          label={t("Salvar posição do mapa")}
          savingLabel={t("Salvando...")}
        />
        <FarmMapDrawingPanel
          t={t}
          farmId={farmId}
          activeTool={drawingTools.activeTool}
          measureOnly={drawingTools.measureOnly}
          pendingShape={drawingTools.pendingShape}
          isAddingHole={drawingTools.isAddingHole}
          savingDrawing={drawingTools.savingDrawing}
          toolsReady={mapReady}
          draftPointCount={drawingTools.draftPointCount}
          draftLineDistanceM={drawingTools.draftLineDistanceM}
          draftLastSegmentM={drawingTools.draftLastSegmentM}
          minDraftPoints={drawingTools.minDraftPoints}
          editingCropId={drawingTools.editingCropId}
          editingSubFarmId={drawingTools.editingSubFarmId}
          onStartTool={drawingTools.startTool}
          onCloseTool={drawingTools.closeTool}
          onClearPending={drawingTools.clearPendingOverlay}
          onClearMeasureDraft={drawingTools.clearMeasureDraft}
          onFinishDraft={drawingTools.finishDraftDrawing}
          onUndoDraftPoint={drawingTools.undoDraftPoint}
          onStartAddingHole={drawingTools.startAddingHole}
          onCancelAddingHole={drawingTools.cancelAddingHole}
          onRemoveHole={drawingTools.removeHoleAt}
          onSaveCrop={handleSaveDrawingCrop}
          onSaveFarm={handleSaveDrawingFarm}
        />
        <div
          className="farm-map-page__map-resize farm-map-page__map-resize--e"
          title={t("Arraste para ampliar o mapa")}
          onMouseDown={(event) => onResizeStart("map-e", event)}
        />
        <div
          className="farm-map-page__map-resize farm-map-page__map-resize--s"
          title={t("Arraste para ampliar o mapa")}
          onMouseDown={(event) => onResizeStart("map-s", event)}
        />
        <div
          className="farm-map-page__map-resize farm-map-page__map-resize--se"
          title={t("Arraste para ampliar o mapa")}
          onMouseDown={(event) => onResizeStart("map-se", event)}
        />
      </div>
    );
  }

  return (
    <div className="farm-map-page">
      <div className="farm-map-page__header">
        <h1>
          <FaCompass style={{ marginRight: 8 }} />
          {t("Mapa da fazenda")}
          <button
            type="button"
            className="farm-map-page__help-btn"
            title={t("Como usar o mapa da fazenda?")}
            aria-label={t("Como usar o mapa da fazenda?")}
            onClick={() => setShowFarmMapHelp(true)}
          >
            <FaInfoCircle />
          </button>
        </h1>
        <div className="farm-map-page__sync-bar">
        <div className="farm-map-page__sync-actions">
          <button
            type="button"
            className={
              publishGuard.hasUnpublishedChanges
                ? "btn btn-primary btn-sm farm-map-page__sync-btn farm-map-page__sync-btn--pending"
                : "btn btn-primary btn-sm farm-map-page__sync-btn"
            }
            onClick={handlePublishFarmMapSync}
            disabled={loading || publishingFarmMapSync}
          >
            {publishingFarmMapSync
              ? t("Publicando...")
              : t("Sincronizar com app de campo")}
          </button>
          {publishGuard.hasUnpublishedChanges ? (
            <span className="farm-map-page__sync-pending-badge">
              {t("Alterações não sincronizadas")}
            </span>
          ) : null}
          <button
            type="button"
            className="farm-map-page__sync-info-btn"
            aria-label={t("Observação sobre sincronização com o app de campo")}
            title={t("Observação sobre sincronização")}
            onClick={() => setShowSyncHelp(true)}
          >
            <FaInfoCircle />
          </button>
        </div>
        <span className="farm-map-page__sync-meta">
          {farmMapSyncRevision > 0
            ? t("Última publicação: revisão {{revision}}", {
                revision: farmMapSyncRevision,
              })
            : t("Ainda não publicado para o app de campo")}
          {farmMapSyncPublishedAt
            ? ` — ${new Date(farmMapSyncPublishedAt).toLocaleString()}`
            : ""}
        </span>
      </div>
      </div>

      {showFarmMapHelp ? (
        <SidebarToolInfoModal
          title={t(FARM_MAP_HELP_TITLE)}
          onClose={() => setShowFarmMapHelp(false)}
        >
          <FarmMapHelpContent />
        </SidebarToolInfoModal>
      ) : null}

      {showSyncHelp ? (
        <SidebarToolInfoModal
          title={t(FARM_MAP_SYNC_HELP_TITLE)}
          onClose={() => setShowSyncHelp(false)}
        >
          <FarmMapSyncHelpContent />
        </SidebarToolInfoModal>
      ) : null}

      <div
        className="farm-map-page__layout"
        style={{ gridTemplateColumns: `${listWidth}px 8px 1fr` }}
      >
        <aside className="farm-map-page__list">
          <div
            className={
              "farm-map-page__ab" +
              (abExpanded ? " is-expanded" : " is-collapsed")
            }
          >
            <button
              type="button"
              className="farm-map-page__ab-toggle"
              onClick={() => {
                const next = !abExpanded;
                setAbExpanded(next);
                try {
                  window.localStorage.setItem(
                    abExpandedStorageKey(farmId),
                    next ? "1" : "0"
                  );
                } catch (error) {
                  /* ignore */
                }
              }}
            >
              {abExpanded ? <FaChevronDown /> : <FaChevronRight />}
              <FaCompass />
              <span>
                {abExpanded
                  ? t("Recomendações Rumo A-B")
                  : t("Rumo A-B — Clique para expandir")}
              </span>
            </button>

            {abExpanded ? (
              <>
                <div className="farm-map-page__list-header">
                  <div className="farm-map-page__list-actions">
                    <button
                      type="button"
                      className="btn btn-success btn-sm"
                      onClick={openCreateModal}
                      disabled={loading || saving}
                    >
                      <FaPlus style={{ marginRight: 4 }} />
                      {t("Novo rumo A-B")}
                    </button>
                    <button
                      type="button"
                      className="btn btn-link btn-sm"
                      onClick={loadData}
                      disabled={loading}
                    >
                      {t("Atualizar")}
                    </button>
                  </div>
                </div>

                {loading ? (
                  <div className="farm-map-page__spinner">
                    <MDSpinner />
                  </div>
                ) : headings.length === 0 ? (
                  <div className="farm-map-page__empty">
                    <p>
                      {t(
                        "Nenhum rumo A-B publicado no mapa. Clique em \"Novo rumo A-B\" para marcar os pontos A e B no satélite e publicar para o app."
                      )}
                    </p>
                    <button
                      type="button"
                      className="btn btn-success"
                      onClick={openCreateModal}
                    >
                      <FaPlus style={{ marginRight: 6 }} />
                      {t("Novo rumo A-B")}
                    </button>
                  </div>
                ) : (
                  <ul className="farm-map-page__items">
                    {headings.map((item) => (
                      <li key={item.id}>
                        <button
                          type="button"
                          className={
                            visibleMapIds.includes(item.id)
                              ? "farm-map-page__item farm-map-page__item--active"
                              : selectedId === item.id
                              ? "farm-map-page__item farm-map-page__item--selected"
                              : "farm-map-page__item"
                          }
                          onClick={() => toggleHeadingVisibility(item.id)}
                        >
                          <span className="farm-map-page__item-title">
                            {item.name}
                          </span>
                          <span className="farm-map-page__item-meta">
                            {item.bearing != null
                              ? `${t("Rumo")} ${Number(item.bearing).toFixed(1)}°`
                              : t("Rumo A-B")}
                            {item.land_plot_names
                              ? ` · ${item.land_plot_names}`
                              : ""}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            ) : null}
          </div>

          <div className="farm-map-page__sidebar-drawings">
            <div className="farm-map-page__sidebar-drawings-header">
              <h3>{t("Sub-fazendas")}</h3>
            </div>
            {groupFarms.filter((item) => item.parent).length === 0 ? (
              <p className="farm-map-page__empty-text">
                {t(
                  "Use Desenhar fazenda no mapa para cadastrar o perímetro e vincular talhões."
                )}
              </p>
            ) : (
              <ul className="farm-map-page__farm-list">
                {groupFarms
                  .filter((item) => item.parent)
                  .map((item) => (
                    <li
                      key={item.id}
                      className={
                        String(serviceFarmId) === String(item.id)
                          ? "is-selected"
                          : ""
                      }
                    >
                      {String(renamingSubFarmId) === String(item.id) ? (
                        <div className="farm-map-page__farm-list-edit">
                          <input
                            type="text"
                            className="form-control input-sm"
                            value={editingSubFarmName}
                            onChange={(event) =>
                              setEditingSubFarmName(event.target.value)
                            }
                            onKeyDown={(event) =>
                              handleSubFarmNameEditKeyDown(event, item.id)
                            }
                            disabled={savingSubFarmRename}
                            autoFocus
                          />
                          <div className="farm-map-page__farm-list-actions">
                            <button
                              type="button"
                              className="btn btn-primary btn-xs"
                              onClick={() => commitEditingSubFarmName(item.id)}
                              disabled={
                                savingSubFarmRename || !editingSubFarmName.trim()
                              }
                            >
                              {t("Salvar")}
                            </button>
                            <button
                              type="button"
                              className="btn btn-default btn-xs"
                              onClick={cancelEditingSubFarm}
                              disabled={savingSubFarmRename}
                            >
                              {t("Cancelar")}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="farm-map-page__farm-list-btn"
                            onClick={() => selectSubFarm(item.id)}
                            onDoubleClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              startRenamingSubFarm(item);
                            }}
                            title={t(
                              "Clique para selecionar. Duplo clique para renomear."
                            )}
                          >
                            {item.nome_da_fazenda}
                            {item.boundary_geojson ? (
                              <span className="farm-map-page__farm-list-meta">
                                {subFarmPlotCounts[item.id] > 0
                                  ? `${subFarmPlotCounts[item.id]} ${t("talhão(ões)")}`
                                  : t("com perímetro")}
                              </span>
                            ) : null}
                          </button>
                          <div className="farm-map-page__farm-list-actions">
                            <button
                              type="button"
                              className="btn-link"
                              title={t("Ajustar perímetro no mapa")}
                              onClick={(event) => {
                                event.stopPropagation();
                                handleStartAdjustSubFarm(item);
                              }}
                              disabled={
                                Boolean(renamingSubFarmId) ||
                                Boolean(drawingTools.editingSubFarmId)
                              }
                            >
                              <FaEdit />
                            </button>
                            <button
                              type="button"
                              className="btn-link-danger"
                              title={t("Excluir sub-fazenda")}
                              onClick={(event) => {
                                event.stopPropagation();
                                requestDeleteSubFarm(item);
                              }}
                              disabled={Boolean(renamingSubFarmId)}
                            >
                              <FaTrashAlt />
                            </button>
                          </div>
                        </>
                      )}
                    </li>
                  ))}
              </ul>
            )}
          </div>

          <div className="farm-map-page__sidebar-drawings">
            <div className="farm-map-page__sidebar-drawings-header">
              <h3>{t("Talhões e medições")}</h3>
              <FarmMapKmlImport
                farmId={farmId}
                t={t}
                disabled={loading || drawingTools.loadingDrawings}
                onImported={async () => {
                  await drawingTools.reloadDrawings();
                  markMapUnpublished();
                }}
              />
            </div>
            <p className="farm-map-page__sidebar-sync-note">
              {t(
                "Talhões, linhas e rumos A-B publicados aparecem automaticamente no mapa da fazenda do app de campo."
              )}
            </p>
            {selectedCropSummary.count > 0 ? (
              <div className="farm-map-page__sidebar-ha-summary">
                <strong>{formatAreaHa(selectedCropSummary.totalHa)}</strong>
                <span>
                  {selectedCropSummary.count}{" "}
                  {selectedCropSummary.count === 1
                    ? t("talhão selecionado")
                    : t("talhões selecionados")}
                </span>
              </div>
            ) : null}
            {highlightedCropIds.length === 1 &&
            !drawingTools.editingCropId &&
            !drawingTools.activeTool ? (
              <div className="farm-map-page__sidebar-crop-actions">
                <button
                  type="button"
                  className="btn btn-default btn-sm"
                  onClick={() => handleStartAddingHoleToCrop(highlightedCropIds[0])}
                >
                  <FaCut style={{ marginRight: 6 }} />
                  {t("Recorte interno")}
                </button>
                <button
                  type="button"
                  className="btn btn-default btn-sm"
                  onClick={() => handleStartAdjustCrop(highlightedCropIds[0])}
                >
                  <FaEdit style={{ marginRight: 6 }} />
                  {t("Ajustar talhão")}
                </button>
              </div>
            ) : null}
            {drawingTools.loadingDrawings ? (
              <MDSpinner size={24} />
            ) : (
              <>
                {drawingTools.cropAreas.length === 0 &&
                drawingTools.savedLines.length === 0 ? (
                  <p className="farm-map-page__empty-text">
                    {t(
                      "Use as ferramentas no mapa para desenhar linhas, medir distâncias e salvar áreas de cultura."
                    )}
                  </p>
                ) : null}
              <div className="farm-map-page__plots-scroll">
                {drawingTools.cropAreas.map((crop) => (
                  <div
                    key={crop.id}
                    className={
                      highlightedCropIds.includes(crop.id)
                        ? "farm-map-page__sidebar-item is-highlighted"
                        : "farm-map-page__sidebar-item"
                    }
                  >
                    <div
                      className="farm-map-page__sidebar-item-main"
                      title={
                        renamingCropId === crop.id
                          ? undefined
                          : t("Clique para selecionar. Duplo clique para renomear.")
                      }
                      onClick={
                        renamingCropId === crop.id
                          ? undefined
                          : () => handleCropClick(crop.id)
                      }
                      onDoubleClick={
                        renamingCropId === crop.id
                          ? undefined
                          : (event) => startEditingCropName(crop, event)
                      }
                    >
                      {renamingCropId === crop.id ? (
                        <input
                          type="text"
                          className="farm-map-page__sidebar-item-edit"
                          value={editingCropName}
                          disabled={savingCropRename}
                          autoFocus
                          onChange={(event) =>
                            setEditingCropName(event.target.value)
                          }
                          onBlur={() => commitEditingCropName(crop.id)}
                          onKeyDown={(event) =>
                            handleCropNameEditKeyDown(event, crop.id)
                          }
                          onClick={(event) => event.stopPropagation()}
                          onDoubleClick={(event) => event.stopPropagation()}
                        />
                      ) : (
                        <span className="farm-map-page__sidebar-item-label">
                          {plotSidebarLabel(crop)}
                        </span>
                      )}
                      <span className="farm-map-page__sidebar-item-ha">
                        {plotSidebarHectares(crop)} ha
                      </span>
                    </div>
                    <div className="farm-map-page__sidebar-item-actions">
                      {highlightedCropIds.includes(crop.id) &&
                      !drawingTools.editingCropId &&
                      !drawingTools.activeTool ? (
                        <>
                          <button
                            type="button"
                            className="btn-link"
                            title={t("Adicionar recorte interno")}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleStartAddingHoleToCrop(crop.id);
                            }}
                            onDoubleClick={(event) => event.stopPropagation()}
                          >
                            <FaCut />
                          </button>
                          <button
                            type="button"
                            className="btn-link"
                            title={t("Ajustar bordas do talhão")}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleStartAdjustCrop(crop.id);
                            }}
                            onDoubleClick={(event) => event.stopPropagation()}
                          >
                            <FaEdit />
                          </button>
                        </>
                      ) : null}
                      <button
                      type="button"
                      className="btn-link-danger"
                      title={t("Apagar talhão")}
                      onClick={(event) => {
                        event.stopPropagation();
                        requestDeleteDrawingCrop(crop.id);
                      }}
                      onDoubleClick={(event) => event.stopPropagation()}
                    >
                      <FaTrashAlt />
                    </button>
                    </div>
                  </div>
                ))}
                {drawingTools.savedLines.map((line) => (
                  <div key={line.id} className="farm-map-page__sidebar-item">
                    <span>
                      {(line.name || t("Linha")).trim()} —{" "}
                      {formatDistanceM(line.length_m)}
                    </span>
                    <button
                      type="button"
                      className="btn-link-danger"
                      title={t("Apagar linha")}
                      onClick={(event) => {
                        event.stopPropagation();
                        requestDeleteDrawingLine(line.id);
                      }}
                    >
                      <FaTrashAlt />
                    </button>
                  </div>
                ))}
              </div>
              </>
            )}
          </div>
        </aside>

        <div
          className="farm-map-page__layout-resize"
          title={t("Arraste para redimensionar o mapa")}
          onMouseDown={(event) => onResizeStart("list", event)}
        />

        <section className="farm-map-page__detail">
          <div className="farm-map-page__detail-header">
            <div>
              <h2>
                {selected
                  ? selected.name
                  : isCreating
                  ? t("Novo rumo A-B")
                  : t("Mapa da fazenda")}
              </h2>
              <p>
                {selected
                  ? `${selected.service_farm_name || ""}${
                      selected.land_plot_names
                        ? ` · ${selected.land_plot_names}`
                        : ""
                    }`
                  : isCreating
                  ? t("Preencha os dados e publique para o app do operador.")
                  : t(
                      "Clique em um rumo A-B na lista para exibir ou ocultar no mapa. Vários rumos podem ficar abertos ao mesmo tempo."
                    )}
              </p>
            </div>
            <div className="farm-map-page__detail-actions">
              <button
                type="button"
                className="btn btn-success"
                onClick={openCreateModal}
                disabled={saving}
              >
                <FaPlus style={{ marginRight: 6 }} />
                {t("Novo rumo A-B")}
              </button>
              {selected ? (
                <>
                  <button
                    type="button"
                    className="btn btn-default"
                    onClick={() => {
                      setMapModalMode("edit");
                      setMapModalOpen(true);
                    }}
                    disabled={saving}
                  >
                    <FaEdit style={{ marginRight: 6 }} />
                    {t("Editar A-B no mapa")}
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleUnpublish}
                    disabled={saving}
                  >
                    <FaTrashAlt style={{ marginRight: 6 }} />
                    {t("Apagar do mapa")}
                  </button>
                </>
              ) : null}
            </div>
          </div>

          {renderFarmMap()}

          {isCreating ? (
            <div className="farm-map-page__editor farm-map-page__create">
              <label htmlFor="farm-map-create-name">{t("Nome do rumo")}</label>
              <input
                id="farm-map-create-name"
                type="text"
                value={createName}
                onChange={(event) => setCreateName(event.target.value)}
                placeholder={t("Nome do rumo (ex.: Talhão 12 — linha norte)")}
              />

              <label htmlFor="farm-map-create-farm">{t("Fazenda do serviço")}</label>
              <select
                id="farm-map-create-farm"
                value={serviceFarmId}
                onChange={(event) => {
                  setServiceFarmId(event.target.value);
                  setLandPlotIds([]);
                }}
              >
                <option value="">{t("Selecione a fazenda")}</option>
                {groupFarms.map((farm) => (
                  <option key={farm.id} value={farm.id}>
                    {farm.nome_da_fazenda}
                  </option>
                ))}
              </select>

              {visibleLandPlots.length > 0 ? (
                <>
                  <label>{t("Talhões deste rumo")}</label>
                  <div className="farm-map-page__plot-list">
                    {visibleLandPlots.map((plot) => (
                      <label key={plot.id} className="farm-map-page__plot-item">
                        <input
                          type="checkbox"
                          checked={landPlotIds.includes(plot.id)}
                          onChange={() => {
                            setLandPlotIds((current) =>
                              current.includes(plot.id)
                                ? current.filter((id) => id !== plot.id)
                                : [...current, plot.id]
                            );
                          }}
                        />
                        {plot.name}
                      </label>
                    ))}
                  </div>
                </>
              ) : null}

              <label htmlFor="farm-map-create-summary">{t("Recomendação")}</label>
              <textarea
                id="farm-map-create-summary"
                rows={4}
                value={createSummary}
                onChange={(event) => setCreateSummary(event.target.value)}
                placeholder={t(
                  "Instruções para o operador sobre este rumo A-B no mapa."
                )}
              />

              <div className="farm-map-page__create-actions">
                <button
                  type="button"
                  className="btn btn-default"
                  onClick={() => {
                    setMapModalMode("create");
                    setMapModalOpen(true);
                  }}
                  disabled={saving}
                >
                  {t("Ajustar A-B no mapa")}
                </button>
                <button
                  type="button"
                  className="btn btn-link"
                  onClick={cancelCreate}
                  disabled={saving}
                >
                  {t("Cancelar")}
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleCreatePublish}
                  disabled={saving}
                >
                  {saving ? t("Salvando...") : t("Publicar no mapa")}
                </button>
              </div>
            </div>
          ) : selected ? (
            <div className="farm-map-page__editor">
              <label htmlFor="farm-map-summary">{t("Recomendação")}</label>
              <textarea
                id="farm-map-summary"
                rows={4}
                value={editSummary}
                onChange={(event) => setEditSummary(event.target.value)}
                placeholder={t(
                  "Instruções para o operador sobre este rumo A-B no mapa."
                )}
              />
              <button
                type="button"
                className="btn btn-success"
                onClick={handleSaveSummary}
                disabled={saving}
              >
                {t("Salvar recomendação")}
              </button>
            </div>
          ) : null}
        </section>
      </div>

      <FarmMapDeleteConfirmModal
        open={Boolean(deleteConfirm)}
        title={
          deleteConfirm && deleteConfirm.type === "line"
            ? t("Apagar linha")
            : deleteConfirm && deleteConfirm.type === "subFarm"
            ? t("Apagar sub-fazenda")
            : t("Apagar talhão")
        }
        message={
          deleteConfirm
            ? deleteConfirm.type === "line"
              ? t(
                  'Deseja realmente apagar a linha "{{name}}"? Esta ação não pode ser desfeita.',
                  { name: deleteConfirm.label }
                )
              : deleteConfirm.type === "subFarm"
              ? t(
                  'Deseja realmente apagar a sub-fazenda "{{name}}"? Os talhões voltarão para a fazenda gestora. Esta ação não pode ser desfeita.',
                  { name: deleteConfirm.label }
                )
              : t(
                  'Deseja realmente apagar o talhão "{{name}}"? Esta ação não pode ser desfeita.',
                  { name: deleteConfirm.label }
                )
            : ""
        }
        cancelLabel={t("Cancelar")}
        confirmLabel={t("Apagar")}
        loading={deletingDrawing}
        onCancel={() => {
          if (!deletingDrawing) {
            setDeleteConfirm(null);
          }
        }}
        onConfirm={handleConfirmDeleteDrawing}
      />

      <TrackHeadingMapModal
        open={mapModalOpen}
        farmId={farmId}
        onClose={() => setMapModalOpen(false)}
        onConfirm={handleMapConfirm}
        initialBearingData={initialBearingData}
        confirmLabel={
          mapModalMode === "create"
            ? t("Usar estes pontos A-B")
            : t("Salvar alterações no mapa")
        }
        t={t}
      />

      <FarmMapSyncLeaveModal
        open={publishGuard.leavePromptOpen}
        t={t}
        loading={publishGuard.leaveActionLoading}
        onStay={publishGuard.stayOnPage}
        onLeaveWithoutSync={publishGuard.leaveWithoutSync}
        onSyncAndLeave={publishGuard.syncAndLeave}
      />

      <ToastContainer position="bottom-right" />
    </div>
  );
}

export default FarmMapTrackHeadings;
